import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Plane, MapPin } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export const PostCard = ({ post, onLikeUpdate, variant = 'default' }) => {
  const { isAuthenticated } = useAuth();
  const [isLiked, setIsLiked] = useState(post.is_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count);
  const [isLiking, setIsLiking] = useState(false);

  const handleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.error('Please sign in to like posts');
      return;
    }

    if (isLiking) return;
    setIsLiking(true);

    try {
      const response = await axios.post(
        `${API}/posts/${post.post_id}/like`,
        {},
        { withCredentials: true }
      );
      
      setIsLiked(response.data.liked);
      setLikesCount(response.data.likes_count);
      
      if (onLikeUpdate) {
        onLikeUpdate(post.post_id, response.data.liked, response.data.likes_count);
      }
    } catch (error) {
      console.error('Like error:', error);
      toast.error('Failed to update like');
    } finally {
      setIsLiking(false);
    }
  };

  const isTall = variant === 'tall' || post.post_id?.charCodeAt(5) % 3 === 0;

  return (
    <Link 
      to={`/post/${post.post_id}`}
      className={`bento-item glass-card block ${isTall ? 'tall' : ''}`}
      data-testid={`post-card-${post.post_id}`}
    >
      <div className={`photo-card ${isTall ? 'portrait' : ''}`}>
        <img
          src={post.image_url}
          alt={post.caption || 'Window view'}
          loading="lazy"
        />
        
        <div className="photo-card-overlay">
          {/* Caption */}
          {post.caption && (
            <p className="text-sm text-white mb-3 line-clamp-2">{post.caption}</p>
          )}

          {/* Flight tags */}
          <div className="flex flex-wrap gap-2 mb-3">
            {post.airline && (
              <span className="flight-tag">
                <Plane className="w-3 h-3" />
                {post.airline}
              </span>
            )}
            {post.departure && post.arrival && (
              <span className="flight-tag">
                {post.departure} → {post.arrival}
              </span>
            )}
            {post.aircraft_type && (
              <span className="flight-tag">{post.aircraft_type}</span>
            )}
          </div>

          {/* Like button */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleLike}
              className={`like-btn ${isLiked ? 'liked' : ''}`}
              disabled={isLiking}
              data-testid={`like-btn-${post.post_id}`}
            >
              <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
              <span>{likesCount}</span>
            </button>

            {post.latitude && post.longitude && (
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <MapPin className="w-3 h-3" />
                <span>View on map</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};
